import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React, { memo } from 'react';

import defaultTimeZone from '@/constants/defaultTimeZone';
import { Props } from '@/pages/Profile/components/DischargeAndRejections/types';
import hideDisqualificationsAndRejections from '@/pages/Profile/utils/hideDisqualificationsAndRejections';
import { EpisodeOfCare } from '@/types/episodeOfCare';
import DataList, { DataRow } from '@/ui/components/DataList';
import Tooltip from '@/ui/components/Tooltip/Tooltip';
import formatStaffName from '@/utils/formatStaffName';

const DischargeAndRejections = ({ person }: Props): JSX.Element | null => {
  const { episode_of_care, program_qualification, time_zone } = person;

  if (!program_qualification || program_qualification?.status === 'active') {
    return null;
  }

  const { disqualification_reasons, intake_rejection_reasons } = program_qualification;

  if (hideDisqualificationsAndRejections(program_qualification, episode_of_care)) {
    return null;
  }

  const renderReasons = (reasons: string[]): JSX.Element => {
    if (reasons.length) {
      return (
        <>
          {reasons.map((reason, index) => {
            return <div key={`reason-${index}`}>{reason}</div>;
          })}
        </>
      );
    }

    return <span>None</span>;
  };

  const DischargedTooltip = ({
    episodeOfCare
  }: {
    episodeOfCare: EpisodeOfCare | null;
  }): JSX.Element | null => {
    if (episodeOfCare) {
      const { discharged_at, discharged_by } = episodeOfCare;

      return (
        <Tooltip
          tooltipContent={
            <div>
              <div>
                <dt className="inline">Discharged by: </dt>
                <dd className="inline">
                  {discharged_by ? formatStaffName({ staff: discharged_by }) : 'None'}
                </dd>
              </div>

              <div>
                <dt className="inline">Discharged at: </dt>
                <dd className="inline">
                  {discharged_at
                    ? new Date(discharged_at).toLocaleString([], {
                        dateStyle: 'medium',
                        timeStyle: 'medium',
                        timeZone: time_zone || defaultTimeZone
                      })
                    : 'None'}
                </dd>
              </div>
            </div>
          }>
          <FontAwesomeIcon className="ml-1 text-gray-500" icon={faInfoCircle} />
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <div>
      <DataList>
        {episode_of_care && episode_of_care.graduation_reason && (
          <DataRow label="Graduation reason">
            {episode_of_care.graduation_reason}
            <DischargedTooltip episodeOfCare={episode_of_care} />
          </DataRow>
        )}

        {episode_of_care && !!episode_of_care.discharge_reasons.length && (
          <DataRow label="Discharge reason">
            <div className="flex items-center">
              {renderReasons(episode_of_care.discharge_reasons)}
              <DischargedTooltip episodeOfCare={episode_of_care} />
            </div>
          </DataRow>
        )}

        {!!disqualification_reasons.length && (
          <DataRow label="Disqualification reason">
            {renderReasons(disqualification_reasons)}
          </DataRow>
        )}

        {!!intake_rejection_reasons.length && (
          <DataRow label="Intake rejection reason">
            {renderReasons(intake_rejection_reasons)}
          </DataRow>
        )}
      </DataList>
    </div>
  );
};

export default memo(DischargeAndRejections);
